import { singleFullVoter } from "../../../services/voterService";
import { normalizeVoter } from "./voterHelpers";

// In-memory store of fully loaded voter lists, shared by every Voter
// Management page for the lifetime of the browser tab. Loading a whole
// assembly takes minutes, so once a list is here all filtering/searching
// happens client-side (useVoterListFetch) and navigating between pages
// reuses it instead of downloading it again.
//
// Writes elsewhere keep it honest: single delete / bulk update / edit patch
// the cached rows in place; add, Excel upload and delete-by-AC can't be
// patched, so they clear it and the next visit reloads from the server.
// Nothing is persisted - a page refresh always starts clean.

const cache = new Map(); // key -> { count, results, loadedAt }
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const voterCacheKey = ({ app_name, party_name, status, assembly_number }, booth_number = null) =>
  JSON.stringify([app_name, party_name, status, assembly_number, booth_number || null]);

export const getCachedVoters = (key) => cache.get(key);

export const setCachedVoters = (key, entry) => {
  cache.set(key, entry);
};

export const subscribeVoterCache = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const invalidateVoterCache = () => {
  cache.clear();
  notify();
};

const updateAllEntries = (updateResults) => {
  cache.forEach((entry, key) => {
    const results = updateResults(entry.results);
    if (results !== entry.results) {
      cache.set(key, { ...entry, results, count: entry.count - (entry.results.length - results.length) });
    }
  });
  notify();
};

export const removeCachedVoter = (id) =>
  updateAllEntries((results) =>
    results.some((voter) => voter.id === id) ? results.filter((voter) => voter.id !== id) : results
  );

// patchFor(voter) returns the fields to merge into that voter, or null to
// leave it untouched.
export const patchCachedVoters = (patchFor) =>
  updateAllEntries((results) => {
    let changed = false;
    const next = results.map((voter) => {
      const patch = patchFor(voter);
      if (!patch) return voter;
      changed = true;
      return { ...voter, ...patch };
    });
    return changed ? next : results;
  });

// After an edit, re-read that one voter from the server and swap it into
// every cached list it appears in.
export const refreshCachedVoter = async ({ id, app_name, party_name }) => {
  if (!cache.size || !id) return;
  try {
    const res = await singleFullVoter({ id, app_name, party_name });
    if (!res?.success) return;
    const { success, status, message, ...record } = res;
    const fresh = normalizeVoter(record);
    patchCachedVoters((voter) => (String(voter.id) === String(id) ? fresh : null));
  } catch (error) {
    // Couldn't re-read it - drop the cache rather than show stale data.
    invalidateVoterCache();
  }
};
